/**
 * 「提案」のカード（ADR-0060、Phase 3-7）。会話の中（答え待ちがあれば琥珀のカード。承認と同じ見た目）と、引き出しの「提案」。
 * 上から: 題・状態 → Jarvis の説明（何を・なぜ・危険・戻し方。LLM の文として字のまま出す）→ 事実（テストの結果・差分・下書き）→
 * 取り込むときのコマンド → ボタン（取り込む・直してほしい・却下）→ 権限の申請（機能とは別のカード・別のボタン）。
 * 取り込む・権限を入れる は「答えを記録する」だけ。main に入れるのは MacBook の pnpm jarvis release、権限は pnpm jarvis apply（署名）。
 * 声の端末・未適用の環境では出ない（Gateway が 403・503 を返す）。
 */
import { ApiError, api } from "./api.js";
import {
  changeLine,
  DECIDE_ERROR_TEXT,
  diffLine,
  inlineProposals,
  nextStep,
  type PermissionCardView,
  type ProposalCardView,
  type ProposalsView,
  testLine,
  trackLine,
  waiting,
} from "./proposals-model.js";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string)[]
) {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  // 文字列は Text ノードとして入れる（Jarvis の文・題を HTML として解釈しない）
  e.append(...children);
  return e;
}

function errorText(e: unknown): string {
  const code = e instanceof ApiError ? e.code : String(e);
  return DECIDE_ERROR_TEXT[code] ?? `答えを送れませんでした（${code}）`;
}

function said(label: string, text: string): HTMLElement[] {
  return [el("dt", {}, label), el("dd", {}, text)];
}

function permissionElement(
  r: PermissionCardView,
  frozen: boolean,
  onDone: () => void,
): HTMLElement {
  const status = el("p", { class: "small muted" });
  const rows: (Node | string)[] = [
    el(
      "p",
      { class: "approval-head" },
      `権限の申請: ${r.capability}`,
      el("span", { class: "risk" }, ` ${r.status_label}${r.applied ? "・入っています" : ""}`),
    ),
    el("pre", { class: "machine" }, changeLine(r)),
    el(
      "p",
      { class: "small" },
      `差分: ${r.patch_path}（権限の版 ${r.from_version} → ${r.to_version}）`,
    ),
    el("p", { class: "small" }, `実績: ${trackLine(r.track_record)}`),
    el("p", { class: "small muted mono" }, `trace ${r.trace_id}`),
    el(
      "div",
      { class: "rationale" },
      el("p", { class: "small muted" }, "Jarvis の根拠（Jarvis が書いた文です）"),
      el(
        "dl",
        { class: "params" },
        ...said("なぜ", r.why),
        ...said("危険", r.risk),
        ...said("戻し方", r.undo),
      ),
    ),
  ];
  if (r.status === "accepted" && !r.applied)
    rows.push(
      el("p", { class: "small" }, "取り込んだ後に MacBook で: ", el("code", {}, r.apply_command)),
    );
  if (r.status === "proposed") {
    const accept = el(
      "button",
      { class: "primary" },
      "入れる（apply します）",
    ) as HTMLButtonElement;
    const reject = el("button", {}, "却下") as HTMLButtonElement;
    const send = async (decision: "accepted" | "rejected") => {
      accept.disabled = reject.disabled = true;
      status.textContent = "記録しています…";
      try {
        const res = await api<{ apply_command?: string }>("/v1/proposals/permission", {
          request_id: r.id,
          decision,
        });
        status.textContent =
          decision === "accepted"
            ? `記録しました。取り込んだ後に MacBook で ${res.apply_command ?? r.apply_command}`
            : "却下しました（同じ中身は、しばらく出しません）。";
        onDone();
      } catch (e) {
        status.textContent = errorText(e);
        accept.disabled = reject.disabled = false;
      }
    };
    accept.onclick = () => void send("accepted");
    reject.onclick = () => void send("rejected");
    if (frozen) accept.disabled = true;
    rows.push(el("div", { class: "row" }, accept, reject));
  }
  rows.push(status);
  return el("div", { class: "approval-item permission-item", "data-request-id": r.id }, ...rows);
}

function proposalElement(p: ProposalCardView, frozen: boolean, onDone: () => void): HTMLElement {
  const status = el("p", { class: "small muted" });
  const files = el(
    "details",
    { class: "closed" },
    el("summary", {}, `差分: ${diffLine(p.diff)}`),
    el(
      "ul",
      { class: "small mono" },
      ...p.diff.files.map((f) =>
        el("li", {}, `${f.path}（+${f.added ?? "?"} −${f.deleted ?? "?"}）`),
      ),
      ...(p.diff.more_files > 0 ? [el("li", {}, `ほか ${p.diff.more_files} ファイル`)] : []),
    ),
  );
  const rows: (Node | string)[] = [
    el(
      "p",
      { class: "approval-head" },
      p.title,
      el("span", { class: "risk" }, ` ${p.status_label}`),
    ),
    el("p", { class: "small muted" }, `${p.kind}・${p.created_at}`),
    el(
      "div",
      { class: "rationale" },
      el("p", { class: "small muted" }, "Jarvis の説明（Jarvis が書いた文です）"),
      el(
        "dl",
        { class: "params" },
        ...said("何を", p.what),
        ...said("なぜ", p.why),
        ...said("危険", p.risks),
        ...said("戻し方", p.undo),
      ),
    ),
    el("p", { class: "small" }, `テスト: ${testLine(p.test_result)}`),
    files,
  ];
  if (p.protected_drafts.length > 0)
    rows.push(
      el(
        "p",
        { class: "small" },
        `保護対象の下書き（取り込んだ後に本人が apply）: ${p.protected_drafts.join("、")}`,
      ),
    );
  rows.push(el("p", { class: "small mono" }, `ブランチ ${p.branch}・trace ${p.trace_id}`));
  const next = nextStep(p);
  if (next) rows.push(el("p", { class: "small flag-soft" }, next));
  if (p.decision_comment)
    rows.push(el("p", { class: "small muted" }, `本人のコメント: ${p.decision_comment}`));
  if (p.status === "proposed") {
    const approve = el("button", { class: "primary" }, "取り込む") as HTMLButtonElement;
    const fix = el("button", {}, "直してほしい") as HTMLButtonElement;
    const reject = el("button", {}, "却下") as HTMLButtonElement;
    const comment = el("textarea", {
      rows: "2",
      placeholder: "直してほしいこと",
      "aria-label": "直してほしいこと",
      hidden: "",
    }) as HTMLTextAreaElement;
    const buttons = [approve, fix, reject];
    const send = async (decision: "approved" | "rejected" | "fix_requested") => {
      for (const b of buttons) b.disabled = true;
      status.textContent = "記録しています…";
      try {
        const res = await api<{ release_command?: string; follow_up?: { how: string } }>(
          "/v1/proposals/decide",
          {
            proposal_id: p.id,
            decision,
            ...(decision === "fix_requested" ? { comment: comment.value } : {}),
          },
        );
        status.textContent =
          decision === "approved"
            ? `記録しました。MacBook で ${res.release_command ?? p.release_command}`
            : decision === "rejected"
              ? "却下しました。"
              : res.follow_up?.how === "amend"
                ? "動いている仕事に伝えました。"
                : "作業係に直しを頼みました（同じブランチで続けます）。";
        onDone();
      } catch (e) {
        status.textContent = errorText(e);
        for (const b of buttons) b.disabled = false;
      }
    };
    approve.onclick = () => void send("approved");
    reject.onclick = () => void send("rejected");
    fix.onclick = () => {
      if (comment.hidden) {
        comment.hidden = false;
        fix.textContent = "直してほしいことを送る";
        comment.focus();
        return;
      }
      if (!comment.value.trim()) {
        status.textContent = DECIDE_ERROR_TEXT.comment_required ?? "";
        return;
      }
      void send("fix_requested");
    };
    if (frozen) approve.disabled = fix.disabled = true;
    rows.push(comment, el("div", { class: "row" }, approve, fix, reject));
  }
  rows.push(status);
  if (p.permissions.length > 0)
    rows.push(
      el(
        "div",
        { class: "permissions" },
        el(
          "p",
          { class: "small muted" },
          "権限の申請（機能とは別に答えます。入れるのは MacBook の pnpm jarvis apply）",
        ),
        ...p.permissions.map((r) => permissionElement(r, frozen, onDone)),
      ),
    );
  return el("div", { class: "approval-item proposal-item", "data-proposal-id": p.id }, ...rows);
}

/**
 * カードを描く。inline: true なら会話の中（答え待ちだけ。無ければ空）、false なら引き出し（最近答えたものも）。
 */
export async function renderProposalsCard(
  root: HTMLElement,
  opts: {
    inline?: boolean;
    onChange?: () => void;
    /** 読めたとき、答え待ちの数を知らせる（見出しの状態の行・数の札に使う） */
    onView?: (v: { waiting: number; open: number; max_open: number }) => void;
  } = {},
): Promise<void> {
  let view: ProposalsView;
  try {
    view = await api<ProposalsView>("/v1/proposals");
  } catch (e) {
    if (!(e instanceof ApiError)) console.warn(e);
    root.replaceChildren();
    return;
  }
  const w = waiting(view);
  opts.onView?.({ waiting: w.proposals + w.permissions, open: view.open, max_open: view.max_open });
  const list = opts.inline ? inlineProposals(view) : view.proposals;
  if (list.length === 0) {
    root.replaceChildren();
    return;
  }
  const redraw = () => {
    void renderProposalsCard(root, opts);
    opts.onChange?.();
  };
  const n = w.proposals + w.permissions;
  root.replaceChildren(
    el(
      "section",
      { class: `card approvals proposals${n > 0 ? " waiting" : ""}` },
      el(
        "h2",
        { class: "jv-latin approvals-title" },
        n > 0 ? `提案に答えてください · ${n} 件` : "提案",
      ),
      el("p", { class: "small muted" }, `提案中 ${view.open} / 上限 ${view.max_open} 件`),
      view.frozen
        ? el(
            "p",
            { class: "small flag" },
            "凍結中のため、今は取り込む・直してほしい・入れる を受けません（却下はできます）。",
          )
        : "",
      ...list.map((p) => proposalElement(p, view.frozen, redraw)),
    ),
  );
}
